package dev.lingvo.site.components.editor

import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

/**
 * The per-plural-category label, and nothing else any more.
 *
 * Source and Target left for [EditorFieldSectionHeadingStyle] when the writing column's two
 * landmarks were promoted to `HeadingSmall`; what is left here is the label that repeats down the
 * plural stack, and it stays at `BodySmall` for a reason that is about that column specifically.
 */
val EditorSectionHeadingStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textPrimary)
        // Stays at BodySmall, though the type-hierarchy pass promoted every other structural
        // heading in the editor to HeadingSmall. Its remaining call sites are the per-plural-category
        // labels that sit in a fixed EDITOR_PLURAL_LABEL_COLUMN_PX (128px) column beside each form's
        // textarea, and promoting them costs real layout: at HeadingSmall, Russian's
        // "Many — 11–99 items" wrapped onto a second line and left the em-dash stranded at the end
        // of the first, while its three siblings still fit. Russian has four plural categories;
        // Arabic has six, in the same 128px.
        //
        // A repeated per-item label is not a once-per-screen heading. That is the same line the QA
        // group titles were left alone on, before `LDisclosure` took that header over entirely.
        .fontSize(TypeScale.BodySmall)
        .fontWeight(700)
        .lineHeight(1.4)
}

/**
 * The Source / Target heading over the writing column.
 *
 * **Separate from [EditorSectionHeadingStyle], because the two are different kinds of heading.**
 * That style's own note explains why it stayed at `BodySmall`: its remaining call sites are the
 * per-plural-category labels, repeated up to six times down a fixed 128px column, where a larger
 * size wraps "Many — 11–99 items" onto a second line. Source and Target are the opposite case —
 * two once-per-screen landmarks over the widest column on the screen, and they are what a
 * translator's eye uses to tell the string they are reading from the string they are writing.
 * Promoting one and not the other needed two styles, not one style with a caller-supplied size.
 */
val EditorFieldSectionHeadingStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textPrimary)
        .displayFont()
        .fontSize(TypeScale.HeadingSmall)
        .fontWeight(700)
        .lineHeight(1.3)
        .gap(Space.SM)
        .styleModifier {
            property("display", "flex")
            property("align-items", "baseline")
            property("flex-wrap", "wrap")
        }
}

/**
 * The language riding on that heading — "Source" then "German", not "Source · German".
 *
 * It was a middot glued between two translated fragments by Kotlin, which gives a translator two
 * halves and no way to say how they join. Here the language is its own element: lighter, smaller,
 * and at normal weight, so the heading reads as one word and the language answers "which one"
 * without competing for it. `nowrap` so a two-word language name breaks before itself rather than
 * down the middle.
 */
val EditorFieldSectionLocaleStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.BodySmall)
        .fontWeight(400)
        .styleModifier { property("white-space", "nowrap") }
}

val EditorTargetLabelStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.BodySmall)
        .fontWeight(700)
        .lineHeight(1.4)
}

val EditorTargetErrorStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().danger)
        .fontSize(TypeScale.Small)
        .lineHeight(1.4)
        .styleModifier { property("overflow-wrap", "anywhere") }
}

val EditorQaFindingStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .maxWidth(100.percent)
        .styleModifier {
            property("box-sizing", "border-box")
            property("white-space", "normal")
            property("overflow-wrap", "anywhere")
        }
}

val EditorPluralPanelStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .minWidth(0.px)
        .gap(Space.MD)
}

/** The plain-language example, riding on the category heading rather than on its own line. */
val EditorPluralExampleStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Small)
        .fontWeight(400)
        // Wrapping to a second line is fine and intended — EDITOR_PLURAL_LABEL_COLUMN_PX says so.
        // Wrapping *inside* the example is not: the longest Russian label, "Many — 11–99 items",
        // needs 134px in a 128px column, and the en-dash in "11–99" is a break opportunity, so it
        // split as "Many — 11-" / "99 items" and the reader met a range cut in half. Held together,
        // the break falls before the dash instead: "Many" / "— 11–99 items".
        //
        // Safe against overflow: the example alone is ~89px, well inside the column, so the only
        // thing nowrap can do here is move the break, not create a scrollbar.
        .styleModifier { property("white-space", "nowrap") }
}

val EditorPluralCompletionStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.BodySmall)
        .fontWeight(700)
}

val EditorPluralStatusStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
        .fontWeight(600)
}

val EditorPlaceholderStyle = CssStyle.base {
    Modifier
        .styleModifier {
            property("display", "inline-flex")
            // The DOM equivalent of wrapping the run in U+2068/U+2069. Inside an Arabic sentence,
            // `%1$s` would otherwise have its `%` and `s` visually separated by the surrounding
            // text, so the translator is shown a string that is not the string the app will render.
            //
            // `isolate` rather than injecting the isolate characters into the text: they would end
            // up in a copy-paste, in a memory lookup, and against a length limit.
            property("unicode-bidi", "isolate")
            property("direction", "ltr")
        }
        .backgroundColor(colorMode.toSitePalette().accentSubtle)
        .color(colorMode.toSitePalette().accent)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().accent.toRgb().copyf(alpha = 0.38f))
        .borderRadius(Radius.Control)
        .padding(leftRight = Space.XS)
        .fontSize(TypeScale.Small)
        .fontWeight(600)
        .lineHeight(1.45)
        .monoFont()
}

/**
 * The plural preview line: a muted caption, not a bordered card.
 *
 * It was a `surfaceSunken` box with its own border, radius and `Space.MD` padding on every side —
 * roughly 48px for one line of text. At up to six copies per key (Arabic) that box was most of
 * what made the plural stack ~421px per form; see the compact layout in `EditorText.kt`'s plural
 * loop. The line is still worth keeping — DESIGN.md calls for "a live preview line" — it just does
 * not need a frame of its own once it sits directly under the field it previews.
 */
val PluralPreviewStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Small)
        .lineHeight(1.4)
        .styleModifier { property("overflow-wrap", "anywhere") }
}

/**
 * The per-form source in the plural stack: a single truncatable reference line beside its field,
 * not a bordered card with its own button row (those controls moved to the shared row above the
 * whole stack — see `EditorSourceActionRow`). Truncated rather than wrapped or scrolled: this is
 * reference material once the translator can already read the source in the row above, and a
 * six-line source card was most of the ~421px a single Arabic plural form used to cost.
 */
val PluralSourceCaptionStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Small)
        .lineHeight(1.4)
        .styleModifier {
            property("white-space", "nowrap")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
        }
}

val ExtraPluralButtonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Colors.Transparent)
            .color(colorMode.toSitePalette().textSecondary)
            .border(0.px, LineStyle.Solid, Colors.Transparent)
            .padding(0.px)
            .fontSize(TypeScale.Small)
            .fontWeight(600)
            .cursor(Cursor.Pointer)
    }
    hover { Modifier.color(colorMode.toSitePalette().accent) }
    focusVisible {
        Modifier.styleModifier {
            property("outline", "2px solid ${colorMode.toSitePalette().accent}")
            property("outline-offset", "2px")
        }
    }
}

val EmptyEditorStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .maxWidth(620.px)
        .padding(topBottom = 96.px)
        .styleModifier {
            property("margin-left", "auto")
            property("margin-right", "auto")
        }
}
